import React, { useEffect, useRef, useState } from 'react';
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";

export const ImageWithBbox = ({ src, points }) => {
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const [naturalSize, setNaturalSize] = useState({ width: null, height: null });
  const [isLoaded, setIsLoaded] = useState(false);
  const [positions, setPositions] = useState([{x: null, y: null}]);

  useEffect(() => {
    const img = imgRef.current;

    if (!img) return;

    const handleLoad = () => {
      setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    };

    if (img.complete) {
      handleLoad();
    } else {
      img.addEventListener('load', handleLoad);
      return () => img.removeEventListener('load', handleLoad);
    }
  }, [src]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      updatePosition();
    });
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [naturalSize, points]);

  const computePosition = (point) => {
    const img = imgRef.current;
    const container = containerRef.current;

    if (!img || !container || !naturalSize.width || !naturalSize.height)
      return;

    const containerRect = container.getBoundingClientRect();
    const naturalRatio = naturalSize.width / naturalSize.height;
    const containerRatio = containerRect.width / containerRect.height;

    let offsetX = 0;
    let offsetY = 0;
    let visibleWidth = containerRect.width;
    let visibleHeight = containerRect.height;

    if (naturalRatio > containerRatio) {
      const scale = containerRect.height / naturalSize.height;
      visibleWidth = naturalSize.width * scale;
      offsetX = (containerRect.width - visibleWidth) / 2;
    } else {
      const scale = containerRect.width / naturalSize.width;
      visibleHeight = naturalSize.height * scale;
      offsetY = (containerRect.height - visibleHeight) / 2;
    }

    const x = offsetX + (point[0] / naturalSize.width) * visibleWidth;
    const y = offsetY + (point[1] / naturalSize.height) * visibleHeight;

    return {x, y};
  };

  const updatePosition = () => {
    const newPositions = [];

    for (const point of points) {
      let position = computePosition([point[0], point[1]]);
      if (!position) continue;
      const {x: x1, y: y1} = position;

      position = computePosition([point[2], point[3]]);
      if (!position) continue;
      const {x: x2, y: y2} = position;

      newPositions.push({x1, y1, x2, y2});
    }
    setPositions(newPositions);

    setIsLoaded(true);
  };

  useEffect(() => {
    updatePosition();
  }, [naturalSize, points]);

  const styles = {
    bboxCorner: {
      position: 'absolute',
      width: '12px',
      height: '12px',
      border: '3px solid red',
      borderRadius: '5px',
      zIndex: 60,
      pointerEvents: 'none',
      transform: 'translate(-50%, -50%)',
    },
  };

  return (
    <div ref={containerRef} style={{position: 'relative', width: '100%', height: '100%', overflow: 'hidden'}}>
      <img
        ref={imgRef}
        src={src}
        alt="overlay"
        style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}}
      />
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        {
          positions.map((position, index) => (
            <React.Fragment key={index}>
              <div style={{...styles.bboxCorner, opacity: isLoaded ? 1 : 0, borderBottom: 'none', borderRight: 'none', borderBottomLeftRadius: 0, borderTopRightRadius: 0,
                left: `${position.x1}px`, top: `${position.y1}px`}} />
              <div style={{...styles.bboxCorner, opacity: isLoaded ? 1 : 0, borderBottom: 'none', borderLeft: 'none', borderBottomRightRadius: 0, borderTopLeftRadius: 0,
                left: `${position.x2}px`, top: `${position.y1}px`}} />
              <div style={{...styles.bboxCorner, opacity: isLoaded ? 1 : 0, borderTop: 'none', borderRight: 'none', borderBottomRightRadius: 0, borderTopLeftRadius: 0,
                left: `${position.x1}px`, top: `${position.y2}px`}} />
              <div style={{...styles.bboxCorner, opacity: isLoaded ? 1 : 0, borderTop: 'none', borderLeft: 'none', borderBottomLeftRadius: 0, borderTopRightRadius: 0,
                left: `${position.x2}px`, top: `${position.y2}px`}} />

              <div className="flex justify-center items-center absolute w-5 h-5 bg-red-500 rounded-full text-xs text-white p-0.5"
                   style={{left: `${position.x2 - 20}px`, top: `${position.y2 - 20}px`}}>
                <ExclamationTriangleIcon/>
              </div>
            </React.Fragment>
          ))
        }
      </div>
    </div>
  );
};
